import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Copy, Check, Users, Package, MessageSquare, Star, Search, Building, 
  FileText, Zap, Sparkles, CreditCard, Settings, Megaphone, ShoppingCart,
  Bookmark, ChevronRight, CheckCircle2, Circle, ArrowRight, Eye, LogIn
} from 'lucide-react';

const BetaTestingPage = () => {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [loggingIn, setLoggingIn] = useState(null);
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const testAccounts = [
    { role: 'Proveedor Demo', email: 'proveedor.demo@gmail.com', password: 'TexHub2024!' },
    { role: 'Fabricante Demo', email: 'fabricante.demo@gmail.com', password: 'TexHub2024!' },
    { role: 'Tester 1', email: 'tester1.texhub@gmail.com', password: 'TexHub2024!' },
    { role: 'Tester 2', email: 'tester2.texhub@gmail.com', password: 'TexHub2024!' },
    { role: 'Tester 3', email: 'tester3.texhub@gmail.com', password: 'TexHub2024!' },
  ];

  const handleQuickLogin = async (email, password, index) => {
    setLoggingIn(index);
    try {
      await login(email, password, true); // Use rememberMe=true for localStorage
      // Small delay to ensure token is saved before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      alert('Error al iniciar sesión. Intenta de nuevo.');
    } finally {
      setLoggingIn(null);
    }
  };

  // Complete testing guide with all features
  const testingGuide = [
    {
      id: 'registro',
      icon: Building,
      title: '1. Registro y Empresa',
      description: 'Crea tu cuenta y registra tu empresa',
      time: '5 min',
      steps: [
        { text: 'Haz clic en "Crear Nueva Cuenta" o usa una cuenta de prueba', link: '/register' },
        { text: 'Completa el formulario de registro (o inicia con Google)', link: null },
        { text: 'En el Dashboard, ve a "Mi Empresa" para registrar tu empresa', link: '/dashboard/company/new' },
        { text: 'Selecciona si eres Proveedor, Fabricante o Ambos', link: null },
        { text: 'Llena los datos: nombre, ubicación, descripción, contacto', link: null },
        { text: 'Sube el logo de tu empresa', link: null },
      ]
    },
    {
      id: 'productos',
      icon: Package,
      title: '2. Catálogo de Productos',
      description: 'Agrega productos a tu catálogo',
      time: '10 min',
      steps: [
        { text: 'Ve a Dashboard → Productos', link: '/dashboard/products' },
        { text: 'Haz clic en "Nuevo Producto"', link: '/dashboard/products/new' },
        { text: 'Llena: nombre, descripción, categoría', link: null },
        { text: 'Agrega precio, MOQ (cantidad mínima) y disponibilidad', link: null },
        { text: 'Sube varias imágenes del producto (se comprimen automáticamente)', link: null },
        { text: 'Guarda y verifica que aparezca en tu lista', link: null },
      ]
    },
    {
      id: 'excedentes',
      icon: Zap,
      title: '3. Excedentes y Saldos',
      description: 'Publica telas en saldo o excedentes de producción',
      time: '5 min',
      steps: [
        { text: 'Ve a Dashboard → Excedentes', link: '/dashboard/surplus' },
        { text: 'Haz clic en "Nuevo Excedente"', link: '/dashboard/surplus/new' },
        { text: 'Selecciona el tipo: Tela en saldo, Producción cancelada, Capacidad de maquila, etc.', link: null },
        { text: 'Agrega cantidad, precio y ubicación', link: null },
        { text: 'Marca si tiene disponibilidad inmediata', link: null },
        { text: 'Explora excedentes de otros proveedores', link: '/surplus' },
      ]
    },
    {
      id: 'busqueda',
      icon: Search,
      title: '4. Buscar Proveedores',
      description: 'Encuentra proveedores y guárdalos',
      time: '5 min',
      steps: [
        { text: 'Haz clic en "Buscar proveedores" en el sidebar', link: '/search' },
        { text: 'Filtra por: tipo de empresa, estado, categoría', link: null },
        { text: 'Haz clic en una empresa para ver su perfil completo', link: null },
        { text: 'Usa el botón de "Guardar" para añadir a favoritos', link: null },
        { text: 'Revisa tus guardados en Dashboard → Guardados', link: '/dashboard/saved' },
      ]
    },
    {
      id: 'cotizaciones',
      icon: FileText,
      title: '5. Cotizaciones',
      description: 'Solicita y responde cotizaciones',
      time: '7 min',
      steps: [
        { text: 'Desde el perfil de un proveedor, haz clic en "Solicitar Cotización"', link: null },
        { text: 'Describe qué producto necesitas, cantidad y especificaciones', link: null },
        { text: 'Envía la cotización', link: null },
        { text: 'Ve a Dashboard → Cotizaciones para ver el estado', link: '/dashboard/quotes' },
        { text: 'Si eres proveedor, responde cotizaciones recibidas con tu oferta', link: null },
      ]
    },
    {
      id: 'mensajes',
      icon: MessageSquare,
      title: '6. Mensajería',
      description: 'Comunícate con otras empresas',
      time: '5 min',
      steps: [
        { text: 'Ve a Dashboard → Mensajes', link: '/dashboard/messages' },
        { text: 'Desde el perfil de una empresa, inicia una conversación', link: null },
        { text: 'Envía mensajes y archivos adjuntos', link: null },
        { text: 'Verifica el contador de mensajes no leídos en el menú', link: null },
      ]
    },
    {
      id: 'blog',
      icon: Megaphone,
      title: '7. Blog Comunitario',
      description: 'Publica qué materiales necesitas',
      time: '5 min',
      steps: [
        { text: 'Ve a Dashboard → Blog Comunitario', link: '/dashboard/blog' },
        { text: 'Haz clic en "Nueva Publicación"', link: null },
        { text: 'Describe qué material o servicio buscas', link: null },
        { text: 'Indica cantidad, presupuesto y urgencia', link: null },
        { text: 'Si eres proveedor, responde a publicaciones de compradores', link: null },
      ]
    },
    {
      id: 'calificaciones',
      icon: Star,
      title: '8. Calificaciones',
      description: 'Califica a empresas con las que trabajaste',
      time: '3 min',
      steps: [
        { text: 'Ve al perfil de una empresa', link: null },
        { text: 'Haz clic en "Calificar"', link: null },
        { text: 'Evalúa criterios como: entrega a tiempo, calidad, comunicación', link: null },
        { text: 'Agrega un comentario y envía', link: null },
      ]
    },
    {
      id: 'equipo',
      icon: Users,
      title: '9. Gestión de Equipo',
      description: 'Invita colaboradores a tu empresa',
      time: '5 min',
      steps: [
        { text: 'Ve a Dashboard → Configuración → pestaña "Mi Equipo"', link: '/dashboard/settings' },
        { text: 'Haz clic en "Invitar Miembro"', link: null },
        { text: 'Ingresa el email y selecciona permisos específicos', link: null },
        { text: 'Comparte el link de invitación', link: null },
        { text: 'El invitado puede aceptar desde ese link', link: null },
      ]
    },
    {
      id: 'privacidad',
      icon: Settings,
      title: '10. Privacidad del Catálogo',
      description: 'Controla quién ve tus productos',
      time: '5 min',
      steps: [
        { text: 'Ve a Dashboard → Configuración → pestaña "Privacidad"', link: '/dashboard/settings' },
        { text: 'Activa "Catálogo Privado" si quieres restringir acceso', link: null },
        { text: 'Aprueba o rechaza solicitudes de acceso', link: null },
        { text: 'En pestaña "Links", crea enlaces compartidos con expiración', link: null },
      ]
    },
    {
      id: 'servicios',
      icon: Settings,
      title: '11. WhatsApp y Citas',
      description: 'Habilita opciones de contacto',
      time: '3 min',
      steps: [
        { text: 'Ve a Dashboard → Configuración → pestaña "Servicios"', link: '/dashboard/settings' },
        { text: 'Activa "Contacto por WhatsApp" e ingresa tu número', link: null },
        { text: 'Activa "Solicitud de Programación" para que te agenden citas', link: null },
        { text: 'Verifica que aparezcan los botones en tu perfil público', link: null },
      ]
    },
    {
      id: 'texvision',
      icon: Sparkles,
      title: '12. TexVision AI ✨',
      description: 'Genera visualizaciones de tus telas en prendas',
      time: '5 min',
      premium: true,
      steps: [
        { text: 'Ve a Dashboard → TexVision AI', link: '/dashboard/texvision' },
        { text: 'Selecciona una tela de tu catálogo', link: null },
        { text: 'Elige tipo de prenda (camisa, vestido, pantalón, etc.)', link: null },
        { text: 'Selecciona modelo (hombre/mujer) y color Pantone', link: null },
        { text: 'Genera la imagen y descárgala', link: null },
        { text: 'Nota: En beta está en modo demo, las imágenes son simuladas', link: null },
      ]
    },
    {
      id: 'suscripcion',
      icon: CreditCard,
      title: '13. Suscripciones',
      description: 'Explora los planes disponibles',
      time: '3 min',
      steps: [
        { text: 'Ve a Dashboard → Suscripción', link: '/dashboard/subscription' },
        { text: 'Compara los 4 planes: Gratis, Básica, Oro, Premium', link: null },
        { text: 'Ve los paquetes adicionales de imágenes IA', link: null },
        { text: 'Explora las opciones de patrocinio', link: null },
        { text: 'Prueba el checkout (usa tarjeta: 4242 4242 4242 4242)', link: null },
      ]
    },
  ];

  // Subscription plans summary
  const plans = [
    { 
      name: 'Gratis', 
      price: '$0', 
      features: ['1 usuario', '5 productos', '10 cotizaciones/mes', 'Sin IA'],
      highlight: false
    },
    { 
      name: 'Básica', 
      price: '$999/mes', 
      features: ['1 usuario', '50 productos', 'Cotizaciones ilimitadas', '5 imágenes IA/mes', 'Estadísticas básicas'],
      highlight: false
    },
    { 
      name: 'Oro', 
      price: '$1,599/mes', 
      features: ['3 usuarios', '100 productos', 'Cotizaciones ilimitadas', '20 imágenes IA/mes', 'Perfil destacado', 'Soporte prioritario'],
      highlight: true
    },
    { 
      name: 'Premium', 
      price: '$2,199/mes', 
      features: ['10 usuarios', 'Productos ilimitados', '100 imágenes IA/mes', 'API personalizada', 'Soporte 24/7'],
      highlight: false
    },
  ];

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header Banner */}
      <div className="bg-amber-500 text-black py-2 text-center font-medium">
        🧪 MODO BETA TESTING - Ayúdanos a probar TexHub antes del lanzamiento oficial
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            TexHub <span className="text-amber-400">Beta</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-6">
            Plataforma B2B para la industria textil mexicana. Conecta proveedores y fabricantes.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                Crear Nueva Cuenta
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Iniciar Sesión
              </Button>
            </Link>
          </div>
        </div>

        {/* Test Accounts */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-400" />
              Cuentas de Prueba - Login Rápido
            </CardTitle>
            <CardDescription className="text-slate-400">
              Haz clic en "Entrar" para iniciar sesión automáticamente, o crea tu propia cuenta
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isAuthenticated ? (
              <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
                <p className="text-green-300 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Ya iniciaste sesión como <strong>{user?.email}</strong>
                </p>
                <Link to="/dashboard">
                  <Button className="mt-2 bg-green-600 hover:bg-green-700">
                    Ir al Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ) : null}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {testAccounts.map((account, index) => (
                <div key={index} className="bg-slate-700/30 border border-slate-600 rounded-lg p-4 hover:border-amber-500/50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-amber-400">{account.role}</span>
                    <Button
                      size="sm"
                      onClick={() => handleQuickLogin(account.email, account.password, index)}
                      disabled={loggingIn !== null || isAuthenticated}
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                    >
                      {loggingIn === index ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                          Entrando...
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <LogIn className="h-4 w-4" /> Entrar
                        </span>
                      )}
                    </Button>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="text-slate-400">
                      <span className="text-slate-500">Email:</span> <code className="text-amber-300">{account.email}</code>
                    </p>
                    <p className="text-slate-400">
                      <span className="text-slate-500">Pass:</span> <code>{account.password}</code>
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-700 text-center">
              <p className="text-slate-400 text-sm mb-2">¿Prefieres crear tu propia cuenta?</p>
              <Link to="/register">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                  Registrarse con Email Propio
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* AI Feature Highlight */}
        <Card className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/30 mb-8">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Sparkles className="h-8 w-8 text-purple-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">✨ TexVision AI - Generación de Imágenes</h3>
                <p className="text-slate-300 mb-3">
                  Visualiza cómo se verán tus telas en prendas reales usando inteligencia artificial. 
                  Selecciona tu tela, elige el tipo de prenda y modelo, ¡y genera imágenes profesionales al instante!
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-purple-500/30 text-purple-200">5 imágenes/mes en Plan Básica</Badge>
                  <Badge className="bg-purple-500/30 text-purple-200">20 imágenes/mes en Plan Oro</Badge>
                  <Badge className="bg-purple-500/30 text-purple-200">100 imágenes/mes en Plan Premium</Badge>
                </div>
                <p className="text-sm text-slate-400 mt-2">
                  ⚠️ En modo beta, las imágenes son simuladas. En producción usaremos IA real.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Testing Guide - Step by Step */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white">📋 Guía de Pruebas Paso a Paso</CardTitle>
            <CardDescription className="text-slate-400">
              Sigue esta guía para probar todas las funcionalidades. Haz clic en cada sección para ver los pasos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {testingGuide.map((section) => (
              <div key={section.id} className="border border-slate-600 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-700/30 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${section.premium ? 'bg-purple-500/20' : 'bg-amber-500/20'}`}>
                      <section.icon className={`h-5 w-5 ${section.premium ? 'text-purple-400' : 'text-amber-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{section.title}</span>
                        {section.premium && <Badge className="bg-purple-500/30 text-purple-200 text-xs">Premium</Badge>}
                      </div>
                      <span className="text-sm text-slate-400">{section.description}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-slate-400 border-slate-500">
                      {section.time}
                    </Badge>
                    <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${expandedSection === section.id ? 'rotate-90' : ''}`} />
                  </div>
                </button>
                
                {expandedSection === section.id && (
                  <div className="px-4 pb-4 pt-2 bg-slate-700/20 border-t border-slate-600">
                    <ul className="space-y-2">
                      {section.steps.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Circle className="h-4 w-4 text-slate-500 mt-1 flex-shrink-0" />
                          <span className="text-slate-300 text-sm">
                            {step.text}
                            {step.link && (
                              <Link to={step.link} className="ml-2 text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">
                                Ir <ArrowRight className="h-3 w-3" />
                              </Link>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Plans Summary */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-amber-400" />
              Planes de Suscripción
            </CardTitle>
            <CardDescription className="text-slate-400">
              Compara las funcionalidades incluidas en cada plan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((plan, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-lg border ${plan.highlight ? 'border-amber-500 bg-amber-500/10' : 'border-slate-600 bg-slate-700/20'}`}
                >
                  <h4 className="font-bold text-lg mb-1">{plan.name}</h4>
                  <p className="text-2xl font-bold text-amber-400 mb-3">{plan.price}</p>
                  <ul className="space-y-1.5">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-slate-300 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {plan.highlight && (
                    <Badge className="mt-3 bg-amber-500 text-black">Más Popular</Badge>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-400 mt-4 text-center">
              💰 Ahorra hasta 17% con pago anual • Los pagos en beta son de prueba (no se cobra)
            </p>
          </CardContent>
        </Card>

        {/* Feedback CTA */}
        <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 mb-8">
          <CardContent className="py-8 text-center">
            <h2 className="text-2xl font-bold mb-2">📝 Tu Feedback es Muy Valioso</h2>
            <p className="text-slate-300 mb-4 max-w-xl mx-auto">
              Encontraste un error? Tienes una sugerencia? Queremos escucharte para mejorar TexHub antes del lanzamiento.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                Enviar Feedback por WhatsApp
              </Button>
              <Button variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10">
                Ver Guía Completa (PDF)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Link to="/dashboard" className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-amber-500/50 transition-colors text-center">
            <Eye className="h-6 w-6 mx-auto mb-2 text-amber-400" />
            <span className="text-sm">Dashboard</span>
          </Link>
          <Link to="/search" className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-amber-500/50 transition-colors text-center">
            <Search className="h-6 w-6 mx-auto mb-2 text-amber-400" />
            <span className="text-sm">Buscar</span>
          </Link>
          <Link to="/surplus" className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-amber-500/50 transition-colors text-center">
            <Zap className="h-6 w-6 mx-auto mb-2 text-amber-400" />
            <span className="text-sm">Excedentes</span>
          </Link>
          <Link to="/dashboard/subscription" className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-amber-500/50 transition-colors text-center">
            <CreditCard className="h-6 w-6 mx-auto mb-2 text-amber-400" />
            <span className="text-sm">Planes</span>
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm">
          <p>TexHub Beta v1.0 • Los datos de prueba serán eliminados antes del lanzamiento oficial</p>
          <p className="mt-1">¿Preguntas? Contacta por WhatsApp: +52 XXX XXX XXXX</p>
        </div>
      </div>
    </div>
  );
};

export default BetaTestingPage;
